import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { api } from '@/api/client'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'

interface FeatureFlag {
  key: string
  label: string
  description?: string | null
  enabled: boolean | null
  overridden?: boolean
}

interface FeaturesTabProps {
  propertyId: string
  isActive: boolean
}

/**
 * Admin features panel for a single property.
 *
 * Renders the full feature flag registry returned by the API and lets admins
 * turn each flag on or off via a toggle, and clear a property override so the
 * listing follows the platform registry default again (\`enabled: null\` on the API).
 *
 * The flag list itself is server-driven: whatever the API reports as known
 * flags is what renders. If we drop a flag from the registry in the backend,
 * it simply stops appearing here.
 */
export function FeaturesTab({ propertyId, isActive }: FeaturesTabProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [busyKey, setBusyKey] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['property-features', propertyId],
    queryFn: () => api.getPropertyFeatures(propertyId),
    staleTime: 30000,
    enabled: !!propertyId && !!isActive,
  })

  const flags: FeatureFlag[] = data?.flags || []

  const mutateFlag = async (flag: FeatureFlag, enabled: boolean | null) => {
    setBusyKey(flag.key)
    try {
      await api.setPropertyFeature(propertyId, flag.key, enabled)
      await queryClient.invalidateQueries({ queryKey: ['property-features', propertyId] })
      const action = enabled === null ? 'reset to default' : enabled ? 'enabled' : 'disabled'
      toast({
        title: `${flag.label} ${action}`,
        description:
          enabled === null
            ? `This property now uses the platform default for ${flag.label}.`
            : `${flag.label} is now ${enabled ? 'visible' : 'hidden'} for owners of this property.`,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined
      toast({
        title: 'Failed to update feature',
        description: message || 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setBusyKey(null)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Features</CardTitle>
          <CardDescription>
            Control which portal features are enabled for owners of this property.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <LoadingSkeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Features</CardTitle>
          <CardDescription>
            Control which portal features are enabled for owners of this property.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ErrorState error={error} onRetry={refetch} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Features</CardTitle>
        <CardDescription>
          Control which portal features are enabled for owners of this property. Overrides take
          effect on the owner&apos;s next page load.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {flags.length === 0 ? (
          <EmptyState message="No feature flags defined" />
        ) : (
          <ul className="divide-y divide-border" data-testid="features-list">
            {flags.map(flag => {
              const busy = busyKey === flag.key
              return (
                <li
                  key={flag.key}
                  className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-text">{flag.label}</span>
                    </div>
                    <p className="mt-1 text-sm text-textMuted">{flag.description}</p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2 sm:pt-0.5">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={flag.enabled ?? undefined}
                      aria-label={flag.label}
                      disabled={busy}
                      data-testid={`feature-toggle-${flag.key}`}
                      onClick={() => mutateFlag(flag, !flag.enabled)}
                      className={cn(
                        'relative inline-flex h-8 w-[46px] shrink-0 cursor-pointer rounded-full border-2 border-transparent p-0.5 transition-colors',
                        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        flag.enabled ? 'bg-mint' : 'bg-surface2'
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          'pointer-events-none inline-block size-6 rounded-full bg-white shadow-xs transition-[transform]',
                          flag.enabled ? 'translate-x-[18px]' : 'translate-x-0'
                        )}
                      />
                      {busy && (
                        <span className="absolute inset-0 flex items-center justify-center rounded-full bg-surface2/70">
                          <Loader2 className="size-4 animate-spin text-mint" aria-hidden />
                        </span>
                      )}
                    </button>
                    {flag.overridden && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        className="h-8 shrink-0 px-2 text-xs text-textMuted"
                        data-testid={`feature-use-default-${flag.key}`}
                        onClick={() => mutateFlag(flag, null)}
                      >
                        Use platform default
                      </Button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
